/** @format */

import {ChangeEvent, KeyboardEvent} from 'react';

import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import {Box, Grid, Typography} from '@mui/material';
import {Trans, useTranslation} from 'react-i18next';
import {v4 as uuidv4} from 'uuid';

import SecondarySmallButton from 'components/buttons/SecondarySmallButton';

import {
  AddButtonWrapper,
  Container,
  Header,
  HeaderLabel,
  RemoveButton,
  RowContainer,
  StyledTextField,
} from './styles';

export interface CpmPlanItem {
  id: string;
  campaignId: string;
  cpm: number;
}

interface CpmPlanEditorProps {
  value: CpmPlanItem[];
  onChange: (value: CpmPlanItem[]) => void;
}

export const CpmPlanEditor = ({value, onChange}: CpmPlanEditorProps) => {
  const {t} = useTranslation();

  const handleCampaignIdChange = (index: number, event: ChangeEvent<HTMLInputElement>) => {
    const newValue = [...value];
    newValue[index] = {...newValue[index], campaignId: event.target.value};
    onChange(newValue);
  };

  const handleCpmChange = (index: number, event: ChangeEvent<HTMLInputElement>) => {
    const newValue = [...value];
    newValue[index] = {...newValue[index], cpm: Number(event.target.value)};
    onChange(newValue);
  };

  const handleRemove = (index: number) => {
    const newValue = value.filter((_, i) => i !== index);
    onChange(newValue);
  };

  const handleAdd = () => {
    onChange([...value, {id: uuidv4(), campaignId: '', cpm: 0}]);
  };

  const handleKeyDown = (index: number, event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (index === value.length - 1) {
        handleAdd();
      }
    }
  };

  return (
    <Container>
      <Header>
        <HeaderLabel>
          <Trans>Плановые CPM по кампаниям</Trans>
        </HeaderLabel>
      </Header>
      {value.length > 0 && (
        <Box sx={{mb: 2}}>
          <Grid container spacing={2}>
            <Grid item xs={5}>
              <Typography color='textSecondary'>
                <Trans>ID кампании</Trans>
              </Typography>
            </Grid>
            <Grid item xs={5}>
              <Typography color='textSecondary'>
                <Trans>Плановый CPM</Trans>
              </Typography>
            </Grid>
            <Grid item xs={2} />
          </Grid>
        </Box>
      )}
      {value.map((item, index) => (
        <RowContainer key={item.id}>
          <StyledTextField
            placeholder={t('ID кампании', 'ID кампании')}
            value={item.campaignId}
            onChange={event =>
              handleCampaignIdChange(index, event as ChangeEvent<HTMLInputElement>)
            }
            onKeyDown={event => handleKeyDown(index, event as KeyboardEvent<HTMLInputElement>)}
            inputProps={{
              'data-testid': `cpm-plan-campaign-id-${index}`,
            }}
          />
          <StyledTextField
            type='number'
            placeholder={t('Плановый CPM', 'Плановый CPM')}
            value={item.cpm ?? ''}
            onChange={event => handleCpmChange(index, event as ChangeEvent<HTMLInputElement>)}
            onKeyDown={event => handleKeyDown(index, event as KeyboardEvent<HTMLInputElement>)}
            inputProps={{
              min: 0,
              'data-testid': `cpm-plan-cpm-${index}`,
            }}
          />
          <RemoveButton
            onClick={() => handleRemove(index)}
            data-testid={`cpm-plan-remove-${index}`}
          >
            <CancelOutlinedIcon />
          </RemoveButton>
        </RowContainer>
      ))}
      <AddButtonWrapper>
        <SecondarySmallButton onClick={handleAdd} data-testid='cpm-plan-add-button'>
          <Trans>Добавить кампанию</Trans>
        </SecondarySmallButton>
      </AddButtonWrapper>
    </Container>
  );
};
